/**
 * @fileoverview Making the share pictures: one finished picture (compose),
 * the set a layout makes, each picture kept by what it was made from
 * (frameCache) so only what changed is drawn again, and whether the browser's
 * share sheet takes the files. Moved out of plant-list/lib/shareFrames (batch
 * 18, A110); nothing in it changed.
 *
 * Where: the share studio (features/share/studio: buildShareFrames,
 * cachedShareFrames, canShareFiles).
 * Settings: SHARE_COLLAGE, SHARE_FRAME_CACHE_SIZE, SHARE_JPEG_QUALITY,
 * SHARE_PEEK_FRACTION (config/tunables/plantList).
 * Words (en/es): plants.share.canvasUnavailable, plants.share.renderFailed
 * (failures only).
 *
 * @module buildFrames
 */

import type { PlantPhoto } from '@/shared/types/photo';
import { t } from '@/shared/i18n/i18nSetup';
import { SHARE_COLLAGE, SHARE_FRAME_CACHE_SIZE, SHARE_JPEG_QUALITY, SHARE_PEEK_FRACTION } from '@/config/tunables/plantList';
import { drawCover, ensureFonts } from './canvasKit';
import { SHARE_SIZES, TEXT_BASE, type ShareFrameInput } from './frameOptions';
import { loadPhoto } from './framePhotos';
import { drawCardLayout, drawCollage, drawSpanBand, stacks } from './frameLayouts';
import { halfSlot } from './slotShape';
import { drawText, signs } from './frameText';

/** The share of the frame's width the peeked photograph gets. */
const PEEK = SHARE_PEEK_FRACTION;

/**
 * One finished picture.
 *
 * @param input - The sharer's choices.
 * @param photo - The picture's photograph (Before & after draws the set's first and last instead).
 * @param index - Its place in the set, for its file name.
 * @param peeked - Peek's photograph at the edge, if any.
 * @param cover - This picture is the collage itself, not one of its photographs.
 * @returns The picture as a JPEG file.
 */
async function compose(
  input: ShareFrameInput, photo: PlantPhoto, index: number, peeked?: PlantPhoto, cover = false
): Promise<File> {
  const { w: W, h: H } = SHARE_SIZES[input.size];
  const U = W / 1080;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(t('plants.share.canvasUnavailable'));

  ctx.fillStyle = '#2b2f26';
  ctx.fillRect(0, 0, W, H);

  /** This picture carries the signature (the set's first). */
  const signed = signs(input, index);
  if (input.layout === 'collage' && cover) {
    await drawCollage(ctx, input, W, H, U);
  } else if (input.layout === 'card') {
    await drawCardLayout(ctx, input, photo, W, H, U, signed);
  } else if (input.layout === 'ba') {
    const first = input.photos[0];
    const last = input.photos[input.photos.length - 1];
    const down = stacks(input.size);
    const { w: halfW, h: halfH } = halfSlot(W, H, down);

    const a = await loadPhoto(input, first.photo_id);
    drawCover(ctx, a, 0, 0, halfW, halfH, input.focusFor(first));
    a.close();
    const b = await loadPhoto(input, last.photo_id);
    drawCover(ctx, b, down ? 0 : halfW, down ? halfH : 0, halfW, halfH, input.focusFor(last));
    b.close();

    // A hairline at the seam, so it reads as two pictures and not one join.
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    if (down) ctx.fillRect(0, halfH - Math.round(3 * U), W, Math.round(6 * U));
    else ctx.fillRect(halfW - Math.round(3 * U), 0, Math.round(6 * U), H);

    drawText(ctx, input, last, W, H, U, signed);
    drawSpanBand(ctx, input, first, last, W, H, U * TEXT_BASE * (input.textScale ?? 1));
  } else {
    const image = await loadPhoto(input, photo.photo_id);
    drawCover(ctx, image, 0, 0, W, H, input.focusFor(photo));
    image.close();

    if (input.layout === 'peek' && peeked) {
      const x = Math.round(W * (1 - PEEK));
      const next = await loadPhoto(input, peeked.photo_id);
      // The edge shows a slice of the next photograph at its crop's own place: not refitted to the slice's shape.
      drawCover(ctx, next, x, 0, W - x, H, { ...input.focusFor(peeked), a: undefined });
      next.close();
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillRect(x - Math.round(3 * U), 0, Math.round(6 * U), H);
    }

    // A collage's photographs after its first picture carry what Details prints (SHARE_COLLAGE.slideWords).
    if (input.layout !== 'collage' || SHARE_COLLAGE.slideWords) drawText(ctx, input, photo, W, H, U, signed);
  }

  const blob = await new Promise<Blob | null>(
    (resolve) => canvas.toBlob(resolve, 'image/jpeg', SHARE_JPEG_QUALITY)
  );
  if (!blob) throw new Error(t('plants.share.renderFailed'));
  return new File([blob], `grow-${input.plantObjId}-${index + 1}.jpg`, { type: 'image/jpeg' });
}

/**
 * What one picture in the set is made of beyond what they all share: its
 * photograph, and for Peek the one at its edge. Before & after makes one
 * picture, of the set's first and last.
 *
 * @param input - The sharer's choices.
 * @returns One entry per picture the set makes, in order.
 */
function planOf(input: ShareFrameInput): { photo: PlantPhoto; peeked?: PlantPhoto; cover?: boolean }[] {
  if (input.photos.length === 0) return [];
  if (input.layout === 'ba') return [{ photo: input.photos[input.photos.length - 1] }];
  // A collage of two or more: the collage first, then each photograph.
  if (input.layout === 'collage' && input.photos.length >= 2) {
    return [{ photo: input.photos[0], cover: true }, ...input.photos.map((photo) => ({ photo }))];
  }
  /*
   * One picture per photograph, in strip order, for every layout but
   * before-and-after. Card and Peek used to make a single picture of the one
   * in view, so choosing five photographs and pressing Share sent one.
   * Peek shows the next photograph in the set at the edge of each, and the
   * last shows the first: there is more behind this one.
   */
  return input.photos.map((photo, index) => ({
    photo,
    peeked: input.layout === 'peek'
      ? input.photos[index + 1] ?? (input.photos.length > 1 ? input.photos[0] : input.peekPhoto)
      : undefined,
  }));
}

/**
 * The key one finished picture is kept under: the set's shared key, and
 * what is this picture's own (its photograph, where it sits, its line, and
 * the photograph at its edge; for Before & after, the first one too).
 *
 * @param input - The sharer's choices, with cacheKey.
 * @param photo - The picture's photograph.
 * @param peeked - Peek's photograph at the edge, if any.
 * @param cover - The picture is the collage itself.
 * @returns The key, or null when the set has no cacheKey.
 */
function frameKey(input: ShareFrameInput, photo: PlantPhoto, peeked?: PlantPhoto, cover = false): string | null {
  if (!input.cacheKey) return null;
  /** A photograph and where it sits in its frame, as part of the key. */
  const at = (p: PlantPhoto) => { const f = input.focusFor(p); return `${p.photo_id}@${f.x},${f.y},${f.z ?? 1},${f.a ?? ''}`; };
  // The collage is made of every photograph, each where it sits.
  if (cover) return [input.cacheKey, 'collage', ...input.photos.slice(0, 9).map(at)].join('|');
  const first = input.layout === 'ba' ? input.photos[0] : undefined;
  return [input.cacheKey, at(photo), input.wordsFor(photo), peeked ? at(peeked) : '', first ? at(first) : ''].join('|');
}

/**
 * A finished picture under the name its place in the set gives it.
 *
 * @param input - The sharer's choices.
 * @param file - The picture, made for any place.
 * @param index - Its place in this set.
 */
function named(input: ShareFrameInput, file: File, index: number): File {
  const name = `grow-${input.plantObjId}-${index + 1}.jpg`;
  return file.name === name ? file : new File([file], name, { type: file.type });
}

/**
 * The whole set from pictures already made, without drawing any: what a
 * photograph tapped out of the story, or back in, needs.
 *
 * @param input - The sharer's choices, with frameCache and cacheKey.
 * @returns The set, or null when any picture in it has still to be made.
 */
export function cachedShareFrames(input: ShareFrameInput): File[] | null {
  const plan = planOf(input);
  if (plan.length === 0 || !input.frameCache) return null;
  const files: File[] = [];
  for (const [index, { photo, peeked, cover }] of plan.entries()) {
    const key = frameKey(input, photo, peeked, cover);
    const file = key ? input.frameCache.get(key) : undefined;
    if (!file) return null;
    files.push(named(input, file, index));
  }
  return files;
}

/**
 * Frames the chosen photographs according to the chosen layout: each
 * picture already made (frameCache) is used again, and only the rest are
 * drawn. Progress counts the pictures being drawn.
 *
 * Runs ahead of the share rather than during it. Both platforms will only open
 * a share sheet while the tap that asked for it is still live, and ten frames
 * take long enough that the permission has lapsed by the time they are ready —
 * so the work happens while the gardener is still choosing, and the tap itself
 * does nothing but hand over files that already exist.
 *
 * @param input - The sharer's choices.
 * @returns The pictures, in order; fewer when cancelled part way.
 */
export async function buildShareFrames(input: ShareFrameInput): Promise<File[]> {
  const plan = planOf(input);
  if (plan.length === 0) return [];
  await ensureFonts();

  const keys = plan.map(({ photo, peeked, cover }) => frameKey(input, photo, peeked, cover));
  const toMake = keys.filter((key) => !key || !input.frameCache?.has(key)).length;
  let made = 0;
  input.onProgress?.(0, toMake);
  const frames: File[] = [];
  // Sequentially: a phone decoding ten full-size photos at once is how a share
  // button becomes a frozen tab.
  for (const [index, { photo, peeked, cover }] of plan.entries()) {
    if (input.isCancelled?.()) return frames;
    const key = keys[index];
    const kept = key ? input.frameCache?.get(key) : undefined;
    if (kept) {
      frames.push(named(input, kept, index));
      continue;
    }
    const file = await compose(input, photo, index, peeked, cover);
    if (key && input.frameCache) {
      input.frameCache.set(key, file);
      // Kept to a few sets' worth: each picture is a few hundred KB.
      while (input.frameCache.size > FRAME_CACHE_SIZE) input.frameCache.delete(input.frameCache.keys().next().value as string);
    }
    frames.push(file);
    made += 1;
    input.onProgress?.(made, toMake);
  }
  return frames;
}

/** How many finished pictures a frameCache keeps. */
const FRAME_CACHE_SIZE = SHARE_FRAME_CACHE_SIZE;

/**
 * Whether this browser can hand files to the share sheet at all.
 *
 * @param files - The finished pictures.
 * @returns True when navigator.canShare accepts them.
 */
export function canShareFiles(files: File[]): boolean {
  return typeof navigator !== 'undefined'
    && typeof navigator.canShare === 'function'
    && navigator.canShare({ files });
}
